import { computed, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import {
  ACTIVITY_REQUEST_DEFAULT_ORDER_BY,
  ACTIVITY_REQUEST_DEFAULT_VIEW,
  ACTIVITY_REQUEST_LAST_QUERY_PREFIX,
  ACTIVITY_REQUEST_LEGS,
  ACTIVITY_REQUEST_ORDER_BY,
  ACTIVITY_REQUEST_PAGE_SIZE,
  ACTIVITY_REQUEST_VIEWS,
  REGISTRATION_MODES
} from '@/constants/activity-requests.constants'
import type {
  ActivityRequestLeg,
  ActivityRequestOrderBy,
  ActivityRequestQuery,
  ActivityRequestQuickViewKey,
  ActivityRequestView,
  RegistrationMode
} from '@/types/activity-requests.types'

import {
  currentUserStorageId,
  readStorageJson,
  writeStorageJson
} from '../utils/activity-request-storage'

/**
 * Filtros da aba Solicitações (spec 2026-09-25 §6.2). É a FONTE ÚNICA de lista, contadores
 * e export: os três leem `query`, então um filtro novo entra aqui uma vez.
 */
export interface ActivityRequestFilters {
  view: ActivityRequestView
  reopened: boolean | null
  healthUnitIds: number[]
  scheduleIds: number[]
  shiftIds: number[]
  operationalGroups: string[]
  registrationModes: RegistrationMode[]
  requestedLeg: ActivityRequestLeg[]
  startDate: string | null
  endDate: string | null
  deadlineFrom: string | null
  deadlineTo: string | null
  hasAntecipe: boolean | null
  hasInCash: boolean | null
  search: string
  orderBy: ActivityRequestOrderBy
}

export type ActivityRequestUrlQuery = Record<string, string | string[]>

export const DEFAULT_ACTIVITY_REQUEST_FILTERS: ActivityRequestFilters = Object.freeze({
  view: ACTIVITY_REQUEST_DEFAULT_VIEW,
  reopened: null,
  healthUnitIds: [],
  scheduleIds: [],
  shiftIds: [],
  operationalGroups: [],
  registrationModes: [],
  requestedLeg: [],
  startDate: null,
  endDate: null,
  deadlineFrom: null,
  deadlineTo: null,
  hasAntecipe: null,
  hasInCash: null,
  search: '',
  orderBy: ACTIVITY_REQUEST_DEFAULT_ORDER_BY
}) as ActivityRequestFilters

/** Chaves que esta aba escreve na URL (o resto — `tab`, `onlyRequests` — é do `Index.vue`). */
export const ACTIVITY_REQUEST_URL_KEYS = [
  'view',
  'reopened',
  'healthUnitIds',
  'scheduleIds',
  'shiftIds',
  'operationalGroups',
  'registrationModes',
  'requestedLeg',
  'startDate',
  'endDate',
  'deadlineFrom',
  'deadlineTo',
  'hasAntecipe',
  'hasInCash',
  'search',
  'orderBy',
  'page'
] as const

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/
const SEARCH_MAX_LENGTH = 100

const first = (value: unknown): string | null => {
  if (Array.isArray(value)) return value.length ? String(value[0]) : null
  return value === undefined || value === null || value === '' ? null : String(value)
}

const list = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  const text = first(value)
  return text
    ? text
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean)
    : []
}

/** Grupos sobem REPETIDOS (nome pode ter vírgula); um valor único vira lista de um. */
const repeated = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.map(String).filter(Boolean)
  const text = first(value)
  return text ? [text] : []
}

const positiveIds = (value: unknown): number[] =>
  list(value)
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0)

const oneOf = <T extends string>(allowed: readonly T[], value: unknown, fallback: T): T => {
  const text = first(value)
  return text && (allowed as readonly string[]).includes(text) ? (text as T) : fallback
}

const subsetOf = <T extends string>(allowed: readonly T[], value: unknown): T[] =>
  list(value).filter((item): item is T => (allowed as readonly string[]).includes(item))

const triState = (value: unknown): boolean | null => {
  const text = first(value)
  if (text === 'true') return true
  if (text === 'false') return false
  return null
}

const isoDay = (value: unknown): string | null => {
  const text = first(value)
  return text && ISO_DAY.test(text) ? text : null
}

const isoInstant = (value: unknown): string | null => {
  const text = first(value)
  if (!text) return null
  const time = Date.parse(text)
  return Number.isNaN(time) ? null : new Date(time).toISOString()
}

const positivePage = (value: unknown): number => {
  const page = Number(first(value))
  return Number.isInteger(page) && page > 0 ? page : 1
}

/** Filtros → URL. Padrão não entra (exceto `view`, que o link compartilhado precisa carregar). */
export function activityRequestFiltersToUrl(
  filters: ActivityRequestFilters,
  page: number
): ActivityRequestUrlQuery {
  const csv = (values: Array<string | number>): string | null =>
    values.length ? values.join(',') : null
  const triText = (value: boolean | null): string | null => (value === null ? null : String(value))

  const entries: Record<string, string | string[] | null> = {
    view: filters.view,
    reopened: triText(filters.reopened),
    healthUnitIds: csv(filters.healthUnitIds),
    scheduleIds: csv(filters.scheduleIds),
    shiftIds: csv(filters.shiftIds),
    // Grupos sobem repetidos: o nome pode ter vírgula.
    operationalGroups: filters.operationalGroups.length ? [...filters.operationalGroups] : null,
    registrationModes: csv(filters.registrationModes),
    requestedLeg: csv(filters.requestedLeg),
    startDate: filters.startDate,
    endDate: filters.endDate,
    deadlineFrom: filters.deadlineFrom,
    deadlineTo: filters.deadlineTo,
    hasAntecipe: triText(filters.hasAntecipe),
    hasInCash: triText(filters.hasInCash),
    search: filters.search.trim() || null,
    orderBy: filters.orderBy !== ACTIVITY_REQUEST_DEFAULT_ORDER_BY ? filters.orderBy : null,
    page: page > 1 ? String(page) : null
  }

  const query: ActivityRequestUrlQuery = {}
  for (const [key, value] of Object.entries(entries)) {
    if (value !== null && value !== '') query[key] = value
  }
  return query
}

/** URL → filtros, tolerante: valor desconhecido cai no padrão em vez de ir cru ao backend. */
export function activityRequestFiltersFromUrl(query: Record<string, unknown>): {
  filters: ActivityRequestFilters
  page: number
} {
  return {
    filters: {
      view: oneOf(ACTIVITY_REQUEST_VIEWS, query.view, ACTIVITY_REQUEST_DEFAULT_VIEW),
      reopened: triState(query.reopened),
      healthUnitIds: positiveIds(query.healthUnitIds),
      scheduleIds: positiveIds(query.scheduleIds),
      shiftIds: positiveIds(query.shiftIds),
      operationalGroups: repeated(query.operationalGroups),
      registrationModes: subsetOf(REGISTRATION_MODES, query.registrationModes),
      requestedLeg: subsetOf(ACTIVITY_REQUEST_LEGS, query.requestedLeg),
      startDate: isoDay(query.startDate),
      endDate: isoDay(query.endDate),
      deadlineFrom: isoInstant(query.deadlineFrom),
      deadlineTo: isoInstant(query.deadlineTo),
      hasAntecipe: triState(query.hasAntecipe),
      hasInCash: triState(query.hasInCash),
      search: (first(query.search) ?? '').trim().slice(0, SEARCH_MAX_LENGTH),
      orderBy: oneOf(ACTIVITY_REQUEST_ORDER_BY, query.orderBy, ACTIVITY_REQUEST_DEFAULT_ORDER_BY)
    },
    page: positivePage(query.page)
  }
}

export const hasActivityRequestUrlKeys = (query: Record<string, unknown>): boolean =>
  ACTIVITY_REQUEST_URL_KEYS.some((key) => query[key] !== undefined)

/** "Mais filtros (n)" — o que mora dentro da gaveta (M1-A balão 4). */
export function countMoreFilters(filters: ActivityRequestFilters): number {
  return [
    filters.deadlineFrom !== null || filters.deadlineTo !== null,
    filters.hasAntecipe !== null,
    filters.hasInCash !== null,
    filters.reopened !== null
  ].filter(Boolean).length
}

/** Há filtro de verdade (view e ordenação são navegação, não filtro — M1 decisão 7). */
export function hasActiveActivityRequestFilters(filters: ActivityRequestFilters): boolean {
  const neutral = (f: ActivityRequestFilters) => JSON.stringify({ ...f, view: null, orderBy: null })
  return neutral(filters) !== neutral(DEFAULT_ACTIVITY_REQUEST_FILTERS)
}

export function quickViewOf(filters: ActivityRequestFilters): ActivityRequestQuickViewKey {
  return filters.view === 'ALL' && filters.reopened === true ? 'REOPENED' : filters.view
}

export function applyQuickView(
  filters: ActivityRequestFilters,
  key: ActivityRequestQuickViewKey
): ActivityRequestFilters {
  if (key === 'REOPENED') return { ...filters, view: 'ALL', reopened: true }
  // Sair de "Reabertas" desliga o recorte que ela mesma ligou.
  const reopened = quickViewOf(filters) === 'REOPENED' ? null : filters.reopened
  return { ...filters, view: key, reopened }
}

export function toActivityRequestQuery(
  filters: ActivityRequestFilters,
  page: number,
  limit: number
): ActivityRequestQuery {
  return {
    healthUnitIds: [...filters.healthUnitIds],
    scheduleIds: [...filters.scheduleIds],
    shiftIds: [...filters.shiftIds],
    operationalGroups: [...filters.operationalGroups],
    registrationModes: [...filters.registrationModes],
    requestedLeg: [...filters.requestedLeg],
    startDate: filters.startDate,
    endDate: filters.endDate,
    deadlineFrom: filters.deadlineFrom,
    deadlineTo: filters.deadlineTo,
    view: filters.view,
    reopened: filters.reopened,
    hasAntecipe: filters.hasAntecipe,
    hasInCash: filters.hasInCash,
    search: filters.search,
    orderBy: filters.orderBy,
    page,
    limit
  }
}

const lastQueryKey = (): string => `${ACTIVITY_REQUEST_LAST_QUERY_PREFIX}${currentUserStorageId()}`

/**
 * Estado ⇄ URL. Prioridade na hidratação: URL > `initial()` (visão salva padrão) > última
 * combinação guardada > padrão. A busca nunca é guardada (M1-B balão 4).
 */
export function useActivityRequestFilters(
  options: { initial?: () => ActivityRequestUrlQuery | null } = {}
) {
  const route = useRoute()
  const router = useRouter()

  const filters = ref<ActivityRequestFilters>({ ...DEFAULT_ACTIVITY_REQUEST_FILTERS })
  const page = ref(1)

  const query = computed(() =>
    toActivityRequestQuery(filters.value, page.value, ACTIVITY_REQUEST_PAGE_SIZE)
  )

  function baseQuery(): Record<string, string> {
    const base: Record<string, string> = { onlyRequests: 'true' }
    const tab = first(route.query.tab)
    if (tab) base.tab = tab
    return base
  }

  function sync(): void {
    const urlQuery = activityRequestFiltersToUrl(filters.value, page.value)
    void router.replace({ query: { ...baseQuery(), ...urlQuery } })
    const remembered = Object.fromEntries(
      Object.entries(urlQuery).filter(([key]) => key !== 'search' && key !== 'page')
    )
    writeStorageJson(lastQueryKey(), remembered)
  }

  function hydrate(): void {
    const current = route.query as Record<string, unknown>
    let source: Record<string, unknown> | null = null
    if (hasActivityRequestUrlKeys(current)) source = current
    else source = options.initial?.() ?? null
    if (!source) {
      const stored = readStorageJson(lastQueryKey())
      source = stored && typeof stored === 'object' ? (stored as Record<string, unknown>) : null
    }
    const parsed = activityRequestFiltersFromUrl(source ?? {})
    // A busca só vale quando veio da URL (link compartilhado); nunca de storage.
    if (source !== current) parsed.filters.search = ''
    filters.value = parsed.filters
    page.value = source === current ? parsed.page : 1
  }

  function update(patch: Partial<ActivityRequestFilters>, opts: { keepPage?: boolean } = {}): void {
    filters.value = { ...filters.value, ...patch }
    if (!opts.keepPage) page.value = 1
    sync()
  }

  function setPage(next: number): void {
    page.value = next > 0 ? next : 1
    sync()
  }

  /** "Limpar filtros" NÃO mexe na visão nem na ordenação (são navegação). */
  function clear(): void {
    filters.value = {
      ...DEFAULT_ACTIVITY_REQUEST_FILTERS,
      view: filters.value.view,
      orderBy: filters.value.orderBy
    }
    page.value = 1
    sync()
  }

  /** Aplica uma visão salva inteira. */
  function replaceAll(next: ActivityRequestFilters): void {
    filters.value = { ...next }
    page.value = 1
    sync()
  }

  return { filters, page, query, update, setPage, clear, replaceAll, hydrate }
}
